import Axios from 'axios'
import { sanitizeZipCodes } from '../utilities/validators'

const LAPOSTE_API_URL = 'https://geo.api.gouv.fr/communes?format=json&geometry=centre&fields=name,codesPostaux,codeDepartement'
const LAPOSTE_SEARCH_CACHE = {}

const addGlobalPostalCodeOfBigCityIfNecessary = (allPostalCodesOfCity) => {
  if (allPostalCodesOfCity.includes('13001')) {
    return ['13000', ...allPostalCodesOfCity]
  }
  if (allPostalCodesOfCity.includes('69001')) {
    return ['69000', ...allPostalCodesOfCity]
  }
  if (allPostalCodesOfCity.includes('75001')) {
    return ['75000', ...allPostalCodesOfCity]
  }
  return allPostalCodesOfCity
}

const lowerCaseAndRemoveAccents = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export const CityToPostalCode = async (city) => {
  if (LAPOSTE_SEARCH_CACHE[city]) return LAPOSTE_SEARCH_CACHE[city]
  try {
    const data = await Axios.get(`${LAPOSTE_API_URL}&nom=${city}`)
    let postalCode = ''
    let allPostalCodesOfCity = []
    let name = city
    if (data && data.data && data.data.length > 0 && data.data[0].codesPostaux.length > 0) {
      if (data.data.length === 1) {
        postalCode = data.data[0].codesPostaux[0]
        name = data.data[0].nom
        allPostalCodesOfCity = data.data[0].codesPostaux
      } else {
        const citiesWithExactName = data.data.filter((cityData) => cityData.nom && (lowerCaseAndRemoveAccents(cityData.nom) === lowerCaseAndRemoveAccents(city)))
        if (citiesWithExactName.length > 0) {
          postalCode = citiesWithExactName[0].codesPostaux[0]
          name = citiesWithExactName[0].nom
          allPostalCodesOfCity = citiesWithExactName.flatMap((cityWithExactName) => cityWithExactName.codesPostaux)
        }
      }
    }
    LAPOSTE_SEARCH_CACHE[city] = { name, postalCode, allPostalCodesOfCity: addGlobalPostalCodeOfBigCityIfNecessary(allPostalCodesOfCity) }
    return LAPOSTE_SEARCH_CACHE[city]
  } catch (err) {
    console.error(`[api.externals.CityToPostalCode] ${err}`)
    return { postalCode: '', allPostalCodesOfCity: [] }
  }
}

export const PostalCodeToCity = async (zipCode) => {
  if (LAPOSTE_SEARCH_CACHE[zipCode]) return LAPOSTE_SEARCH_CACHE[zipCode]
  try {
    let name = ''
    let allPostalCodesOfCity = []
    const pcSearch = await Axios.get(`${LAPOSTE_API_URL}&codePostal=${zipCode}`)
    if (pcSearch && pcSearch.data && pcSearch.data.length > 0) {
      name = pcSearch.data[0].nom
      allPostalCodesOfCity = pcSearch.data[0].codesPostaux
    }
    if (!name && zipCode.indexOf('000') === 2) {
      const deptSearch = await Axios.get(`${LAPOSTE_API_URL}&codePostal=${zipCode.slice(0, 4)}1`)
      if (deptSearch && deptSearch.data && deptSearch.data.length > 0) {
        name = deptSearch.data[0].nom
        allPostalCodesOfCity = deptSearch.data[0].codesPostaux
      }
    }
    LAPOSTE_SEARCH_CACHE[zipCode] = { name, allPostalCodesOfCity: addGlobalPostalCodeOfBigCityIfNecessary(allPostalCodesOfCity) }
    return LAPOSTE_SEARCH_CACHE[zipCode]
  } catch (err) {
    console.error(`[api.externals.PostalCodeToCity] ${err}`)
    return { name: '', allPostalCodesOfCity: [] }
  }
}

export const CityAndPostalCodeSearch = async ({ city, zipCode }) => {
  const cacheKey = `${zipCode}${city}`.toLowerCase()
  if (LAPOSTE_SEARCH_CACHE[cacheKey]) {
    return LAPOSTE_SEARCH_CACHE[cacheKey]
  }

  try {
    const data = await Axios.get(`${LAPOSTE_API_URL}&nom=${city}&codePostal=${zipCode}`)
    let name = city
    let allPostalCodesOfCity = []
    let postalCode = zipCode

    if (data && data.data && data.data.length > 0) {
      const firstResult = data.data[0]

      // Check if the postal code is one of the codes for this city
      if (firstResult.codesPostaux && firstResult.codesPostaux.includes(zipCode)) {
        postalCode = zipCode
        name = firstResult.nom
        allPostalCodesOfCity = firstResult.codesPostaux
      } else {
        // The zip code does not correspond to this city
        return undefined
      }

      // If multiple results, search for an exact name match
      if (data.data.length > 1) {
        const exactMatch = data.data.find((cityData) =>
          cityData.nom && lowerCaseAndRemoveAccents(cityData.nom) === lowerCaseAndRemoveAccents(city)
        )
        if (exactMatch && exactMatch.codesPostaux.includes(zipCode)) {
          name = exactMatch.nom
          allPostalCodesOfCity = exactMatch.codesPostaux
        }
      }
    } else {
      return undefined
    }

    const result = {
      postalCode,
      name,
      allPostalCodesOfCity: addGlobalPostalCodeOfBigCityIfNecessary(allPostalCodesOfCity)
    }

    LAPOSTE_SEARCH_CACHE[cacheKey] = result
    return result
  } catch (err) {
    console.error(`[api.externals.CityAndPostalCodeSearch] ${err}`)
    return undefined
  }
}

/**
 * Resolves each zip code to the city it belongs to.
 * A professional covers zip codes from several communes, so a listing link must
 * use the commune of its own zip code — the one declared in sitemap.xml — and
 * not the professional's city. Built on PostalCodeToCity so the resolution (and
 * its cache) stays the same as the one used by the search.
 * Returns a { zipCode: cityName } map; unresolved codes are absent.
 */
export const ZipCodesToCities = async (zipCodes) => {
  const cities = {}
  await Promise.all(sanitizeZipCodes(zipCodes).map(async (zipCode) => {
    const res = await PostalCodeToCity(zipCode)
    if (res && res.name) {
      cities[zipCode] = res.name
    }
  }))
  return cities
}



// WEBPACK FOOTER //
// ./src/api/externals.js